/**
 * Keyboard-first navigation for the flat list view (Operator Lists v2, Task 7).
 *
 * Pure hook — no DOM listeners of its own. The caller attaches
 * `handlers.onKeyDown` to a focusable container (the table wrapper,
 * `tabIndex={0}`) so arrow/Enter/`/`/`n` work once that container has focus,
 * without hijacking typing in the toolbar's search box or any other
 * input/textarea/select/contentEditable field.
 */
import { useCallback, useEffect, useState } from 'react'

export interface UseListKeyboardNavOptions {
  /** Number of rows currently addressable (the list's `total`). */
  rowCount: number
  /** Enter on the active row. Only called when activeIndex is in [0, rowCount). */
  onOpen: (index: number) => void
  /** `n` — open the create dialog. */
  onNew: () => void
  /** `/` — focus the toolbar search field. */
  onFocusSearch: () => void
  /** Space — toggle selection at the active index. Omitted (or unset) when the list isn't selectable, in which case Space stays a no-op. */
  onToggleSelect?: (index: number) => void
}

export interface UseListKeyboardNavResult {
  activeIndex: number
  handlers: {
    onKeyDown: (e: React.KeyboardEvent) => void
  }
}

/** Clamp to [0, rowCount-1], or -1 when there are no rows. */
function clampIndex(index: number, rowCount: number): number {
  if (rowCount <= 0) return -1
  if (index < 0) return 0
  if (index > rowCount - 1) return rowCount - 1
  return index
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable
}

export function useListKeyboardNav({
  rowCount,
  onOpen,
  onNew,
  onFocusSearch,
  onToggleSelect,
}: UseListKeyboardNavOptions): UseListKeyboardNavResult {
  const [activeIndex, setActiveIndex] = useState<number>(() => clampIndex(0, rowCount))

  // Re-clamp when the addressable row count changes (filter/search narrows the
  // set, data reloads, etc.) so a stale index doesn't point past the new end.
  useEffect(() => {
    setActiveIndex((prev) => clampIndex(prev, rowCount))
  }, [rowCount])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (isEditableTarget(e.target)) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setActiveIndex((prev) => clampIndex(prev + 1, rowCount))
          return
        case 'ArrowUp':
          e.preventDefault()
          setActiveIndex((prev) => clampIndex(prev - 1, rowCount))
          return
        case 'Enter':
          e.preventDefault()
          if (activeIndex >= 0 && activeIndex < rowCount) onOpen(activeIndex)
          return
        case '/':
          e.preventDefault()
          onFocusSearch()
          return
        case 'n':
          e.preventDefault()
          onNew()
          return
        case ' ':
          // Only take over Space when the caller made the list selectable.
          // Otherwise stay a deliberate no-op and do NOT preventDefault, so
          // the page's default Space behavior (e.g. scroll) stays intact.
          if (onToggleSelect) {
            e.preventDefault()
            onToggleSelect(activeIndex)
          }
          return
        default:
          return
      }
    },
    [rowCount, activeIndex, onOpen, onNew, onFocusSearch, onToggleSelect],
  )

  return { activeIndex, handlers: { onKeyDown } }
}
